import { useEffect, useState } from "react";
import { useAppSelector } from "../../app/hooks";
import { isHigherThanDistrictAdmin } from "../../app/userSlice";
import Select from "../../components/input/Select";
import SearchFilter from "../../components/input/SearchFilter";
import { Loading } from "../../components/Loading";
import SimplePagination from "../../components/SimplePagination";
import AddCourseByAdmin from "../courses/AddCourseByAdmin";
import SchoolCoursesTable from "./SchoolCoursesTable";
import {
	CourseListQueryModel,
	CourseListViewModel,
	useSchoolCoursesListQuery,
} from "./schoolsApiSlice";
import DeleteCourseButton from "./DeleteCourseButton";

interface SchoolCoursesProps {
	schoolId: number;
	canAddCourse: boolean;
	setErrorMessage: (message: string) => void;
}

export default function SchoolCoursesSection(props: SchoolCoursesProps) {
	const { role } = useAppSelector((state) => state.user);

	const _displayLength = 5;
	const initQueryargs: CourseListQueryModel = {
		iDisplayStart: 0,
		iDisplayLength: _displayLength,
		iSortCol_0: 0,
		sSortDir_0: "desc",
		school_id: props.schoolId,
		search_filter: "",
		active_filter: "true",
	};

	const [queryargs, setQueryargs] = useState(initQueryargs);
	const [page, setPage] = useState(0);
	const [searchFilter, setSearchFilter] = useState("");
	const [statusFilter, setStatusFilter] = useState("true");
	const [isFiltered, setIsFiltered] = useState(false);
	const [selectedCourses, setSelectedCourses] = useState<CourseListViewModel[]>([]);

	const schoolCoursesResult = useSchoolCoursesListQuery(queryargs);
	const courses = schoolCoursesResult.data;

	useEffect(() => {
		setQueryargs({
			...queryargs,
			iDisplayStart: _displayLength * page,
			search_filter: searchFilter,
			active_filter: statusFilter,
		});
	}, [page, searchFilter, statusFilter]);

	useEffect(() => {
		setIsFiltered(!!searchFilter || JSON.parse(statusFilter));
		setPage(0);
	}, [searchFilter, statusFilter]);

	const handlePageChange = (page: number) => {
		setPage(page);
	};

	function resetTable() {
		setSelectedCourses([]);
		setPage(0);
	}

	return (
		<section>
			<hr className="my-6" />
			<h2>Courses</h2>
			<div className="flex justify-between items-end mt-4 gap-3">
				<div className="flex gap-3 items-end">
					<Select
						value={statusFilter}
						setValue={(v) => setStatusFilter(v as string)}
						label="Status"
						simpleListBoxId="statusFilter"
						options={[
							{
								value: "true",
								label: "Active Courses",
							},
							{
								value: "false",
								label: "All Courses",
							},
						]}
						fieldClasses="w-56"
					/>
					<SearchFilter
						id="courseSearch"
						name="courseSearch"
						searchFilter={searchFilter}
						setSearchFilter={setSearchFilter}
						label="Search"
						placeHolder="Search by name or referral code..."
						className="w-80"
					/>
				</div>
				<div className="flex gap-3 items-end">
					{props.canAddCourse && <AddCourseByAdmin schoolId={props.schoolId} />}
					{isHigherThanDistrictAdmin(role!) && (
						<DeleteCourseButton
							numSelectedCourses={selectedCourses.length}
							selectedCourseIds={selectedCourses.map((c) => c.courseId)}
							setErrorMessage={props.setErrorMessage}
							resetTable={resetTable}
						/>
					)}
				</div>
			</div>
			<Loading loading={schoolCoursesResult.isLoading} />
			{courses && (
				<SchoolCoursesTable
					courses={courses.aaData}
					selectedCourses={selectedCourses}
					setSelectedCourses={setSelectedCourses}
				/>
			)}
			<SimplePagination
				itemCount={courses?.iTotalDisplayRecords}
				page={page}
				pageSize={queryargs.iDisplayLength}
				totalCount={courses?.iTotalRecords}
				isFiltered={isFiltered}
				onChange={(page) => handlePageChange(page)}
				className={"border-none"}
			/>
		</section>
	);
}
